import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Stack,
  Chip,
  Button,
  Paper,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/UserProvider";
import MuPb from "../../widgets/MuPb";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, saveToken, toastError } from "../../utils/util";
import { usePhoneNumber } from "../../context/PhoneNumberContext";

// ─── Lucide-style SVG icon for "Switch" ───────────────────────────────────────
const SwitchIcon = ({ size = 18, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 3l4 4-4 4" />
    <path d="M20 7H4" />
    <path d="M8 21l-4-4 4-4" />
    <path d="M4 17h16" />
  </svg>
);

const InfoIcon = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const UserCheckIcon = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);

const ContactsIcon = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SheetIcon = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </svg>
);

// ─── Switch Primary Account Modal ─────────────────────────────────────────────
const SwitchPrimaryModal = ({
  open,
  onClose,
  accounts,
  accountDetails,
  detailsLoading,
  currentCustomerId,
}) => {
  const [selectedAccount, setSelectedAccount] = useState("");
  const [migrateUser, setMigrateUser] = useState(true);
  const [migrateContacts, setMigrateContacts] = useState(true);
  const [migrateSheets, setMigrateSheets] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedAccount) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_ENDPOINT}/user/switch-primary-account`,
        {
          newPrimaryCustomerId: selectedAccount,
          migrateUser,
          migrateContacts,
          migrateGoogleSheets: migrateSheets,
        },
        getHeader()
      );
      onClose();
      // Logout after success
      document.location.href = "/logout";
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const displayList = detailsLoading
    ? accounts.map((id) => ({ id, customerId: id, display_phone_number: null }))
    : accountDetails;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 24px 48px rgba(0,0,0,0.14)",
          overflow: "visible",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 1.5,
          pt: 2.5,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 0.8,
              bgcolor: "primary.50",
              borderRadius: 1.5,
              display: "flex",
              color: "primary.main",
            }}
          >
            <SwitchIcon size={18} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
              Switch Primary Account
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select an account to become the new primary
            </Typography>
          </Box>
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
        {/* Logout Warning */}
        <Alert
          severity="warning"
          icon={<InfoIcon size={16} color="inherit" />}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            fontSize: "0.8rem",
            "& .MuiAlert-message": { fontSize: "0.8rem" },
          }}
        >
          <strong>You will be logged out</strong> after this operation
          completes. Please ensure all unsaved work is saved before proceeding.
        </Alert>

        {/* Account Selection */}
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Select New Primary
        </Typography>

        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2.5 }}>
          <InputLabel id="switch-primary-label">Customer ID</InputLabel>
          <Select
            labelId="switch-primary-label"
            value={selectedAccount}
            label="Customer ID"
            onChange={(e) => setSelectedAccount(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            {displayList.map((acc) => {
              const id = acc.customerId || acc.id;
              return (
                <MenuItem key={id} value={id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AccountBalanceIcon
                      sx={{ fontSize: 16, color: "primary.main" }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {id}
                    </Typography>
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        {/* Migration Options */}
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Migration Settings
        </Typography>

        <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }}>
          {[
            {
              key: "user",
              icon: <UserCheckIcon size={15} />,
              label: "Migrate current logged-in user",
              sublabel: "Transfer your user profile to the new primary account",
              value: migrateUser,
              setter: setMigrateUser,
            },
            {
              key: "contacts",
              icon: <ContactsIcon size={15} />,
              label: "Migrate contacts list",
              sublabel: "Move all contacts from your current account",
              value: migrateContacts,
              setter: setMigrateContacts,
            },
            {
              key: "sheets",
              icon: <SheetIcon size={15} />,
              label: "Migrate Google Sheet settings",
              sublabel: "Transfer linked sheet configurations and mappings",
              value: migrateSheets,
              setter: setMigrateSheets,
            },
          ].map(({ key, icon, label, sublabel, value, setter }) => (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                borderColor: value ? "primary.main" : "divider",
                bgcolor: value ? "primary.50" : "background.paper",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onClick={() => setter(!value)}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Checkbox
                  checked={value}
                  onChange={(e) => setter(e.target.checked)}
                  size="small"
                  sx={{ p: 0 }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Box
                  sx={{
                    p: 0.75,
                    bgcolor: value ? "primary.100" : "grey.100",
                    borderRadius: 1.5,
                    display: "flex",
                    color: value ? "primary.main" : "text.secondary",
                  }}
                >
                  {icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500} lineHeight={1.3}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sublabel}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>

        {/* Recommendation note */}
        <Box
          sx={{
            mt: 1.5,
            mb: 2,
            p: 1.5,
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.200",
            borderRadius: 2,
            display: "flex",
            gap: 1,
          }}
        >
          <Box sx={{ color: "info.main", flexShrink: 0, mt: 0.1 }}>
            <InfoIcon size={14} />
          </Box>
          <Typography variant="caption" color="info.dark" lineHeight={1.5}>
            <strong>Recommended:</strong> Enable all three options for the
            smoothest transition. This ensures no data or configuration is lost
            when switching your primary account.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          size="small"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!selectedAccount || submitting}
          onClick={handleSubmit}
          startIcon={<SwitchIcon size={14} color="currentColor" />}
          sx={{ minWidth: 140 }}
        >
          {submitting ? "Switching..." : "Switch & Logout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AccountsList = () => {
  const { openPhoneDialog } = usePhoneNumber();
  const { user: userData, loading } = useContext(UserContext);
  const [accountDetails, setAccountDetails] = useState([]);
  const [loggingIn, setLoggingIn] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [switchModalOpen, setSwitchModalOpen] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!userData) return;
      if (!userData.accounts || userData.accounts.length === 0) return;
      setDetailsLoading(true);
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/user/get-my-number-details?accounts=${userData.accounts.join(
            ","
          )}`,
          getHeader()
        );
        setAccountDetails(response.data || []);
      } catch (err) {
        console.error("Failed to fetch account details:", err);
      } finally {
        setDetailsLoading(false);
      }
    };
    fetchDetails();
  }, [userData]);

  const handleAccountLogin = useCallback(async (loginId) => {
    try {
      setLoggingIn(loginId);
      const response = await axios.post(
        `${API_ENDPOINT}/user/associated-account-login`,
        { customerId: loginId },
        getHeader()
      );
      saveToken(response.data.token);
      document.location.href = "/home";
    } catch (error) {
      toastError(error);
    } finally {
      setLoggingIn(null);
    }
  }, []);

  if (loading) return <MuPb />;

  if (!userData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No user data found
        </Typography>
      </Box>
    );
  }

  if ((userData.accounts ?? []).length === 0) {
    return (
      <Box sx={{ maxWidth: 440, mx: "auto", p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <AccountBalanceIcon
            sx={{ fontSize: 52, color: "text.secondary", opacity: 0.5, mb: 2 }}
          />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No additional phone numbers
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2.5 }}>
            Add a phone number to enhance your account security and
            accessibility.
          </Typography>
          <Button
            onClick={openPhoneDialog}
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
          >
            Add Phone Number
          </Button>
        </Paper>
      </Box>
    );
  }

  const displayList = detailsLoading
    ? userData.accounts.map((id) => ({
        id,
        customerId: id,
        display_phone_number: null,
        quality_rating: null,
        whatsapp_business_manager_messaging_limit: null,
      }))
    : accountDetails;

  return (
    <Box sx={{ p: 2.5, maxWidth: 680 }}>
      {/* ── Header Card ── */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          border: "1px solid",
          borderColor: "primary.100",
          borderRadius: 2,
          bgcolor: "primary.50",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                p: 0.75,
                bgcolor: "primary.100",
                borderRadius: 1.5,
                display: "flex",
                color: "primary.main",
              }}
            >
              {userData.userType === "Admin" ? (
                <BusinessIcon sx={{ fontSize: 18 }} />
              ) : (
                <PersonIcon sx={{ fontSize: 18 }} />
              )}
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1}>
                  {userData.customerId}
                </Typography>
                <Chip
                  label="Primary"
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
                <Chip
                  label={userData.userType}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {userData.accounts.length} associated account
                {userData.accounts.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Stack>

          {/* Switch Button */}
          <Tooltip title="Switch Primary Account" arrow>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSwitchModalOpen(true)}
              startIcon={<SwitchIcon size={14} />}
              sx={{
                borderRadius: 2,
                fontSize: "0.75rem",
                px: 1.5,
                py: 0.6,
                borderColor: "primary.300",
                color: "primary.main",
                "&:hover": {
                  bgcolor: "primary.50",
                  borderColor: "primary.main",
                },
              }}
            >
              Switch
            </Button>
          </Tooltip>
        </Stack>
      </Paper>

      {/* ── Account Rows ── */}
      <Typography
        variant="caption"
        fontWeight={600}
        color="text.secondary"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1,
          display: "block",
        }}
      >
        Associated Accounts
      </Typography>

      <Stack spacing={1}>
        {displayList.map((account) => {
          const id = account.customerId || account.id;
          const isLoggingIn = loggingIn === id;

          return (
            <Card
              key={id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                transition: "box-shadow 0.15s, border-color 0.15s",
                "&:hover": { boxShadow: 2, borderColor: "primary.main" },
              }}
            >
              <CardContent sx={{ p: "10px 14px !important" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Left: Icon + Info */}
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        p: 0.75,
                        bgcolor: "primary.50",
                        borderRadius: 1.5,
                        display: "flex",
                        color: "primary.main",
                        flexShrink: 0,
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        lineHeight={1.2}
                      >
                        {id}
                      </Typography>
                      {detailsLoading ? (
                        <Skeleton width={110} height={14} />
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          lineHeight={1.4}
                        >
                          {account.display_phone_number || "—"}
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        mt={0.5}
                        flexWrap="wrap"
                      >
                        {detailsLoading ? (
                          <>
                            <Skeleton
                              width={55}
                              height={20}
                              sx={{ borderRadius: 2 }}
                            />
                            <Skeleton
                              width={70}
                              height={20}
                              sx={{ borderRadius: 2 }}
                            />
                          </>
                        ) : (
                          <>
                            {account.quality_rating && (
                              <Chip
                                label={`Quality: ${account.quality_rating}`}
                                size="small"
                                color={
                                  account.quality_rating === "GREEN"
                                    ? "success"
                                    : "warning"
                                }
                                sx={{ height: 18, fontSize: "0.62rem" }}
                              />
                            )}
                            {account.whatsapp_business_manager_messaging_limit && (
                              <Chip
                                label={account.whatsapp_business_manager_messaging_limit.replace(
                                  "TIER_",
                                  "Limit: "
                                )}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: "0.62rem" }}
                              />
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Right: Actions */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexShrink={0}
                  >
                    <Tooltip title="View Details" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => console.log("View:", id)}
                      >
                        <VisibilityIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={isLoggingIn || !!loggingIn}
                      onClick={() => handleAccountLogin(id)}
                      sx={{
                        fontSize: "0.72rem",
                        px: 1.5,
                        py: 0.5,
                        minWidth: 72,
                        borderRadius: 1.5,
                      }}
                    >
                      {isLoggingIn ? "..." : "Login"}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* ── Switch Modal ── */}
      <SwitchPrimaryModal
        open={switchModalOpen}
        onClose={() => setSwitchModalOpen(false)}
        accounts={userData.accounts}
        accountDetails={accountDetails}
        detailsLoading={detailsLoading}
        currentCustomerId={userData.customerId}
      />
    </Box>
  );
};

export default AccountsList;
